import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AccountBalanceWalletOutlined as SoldeIcon,
  Add as AddIcon,
  Clear as ClearIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  NorthEast as InIcon,
  PrintOutlined as PrintIcon,
  Remove as RemoveIcon,
  Search as SearchIcon,
  SouthWest as OutIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import AlertDialog from '../../../components/AlertDialog';
import { GlassSurface } from '../../../components/GlassDashboard';
import useAlert from '../../../hooks/useAlert';
import usePdfPreview from '../../../hooks/usePdfPreview';
import { usePermissions } from '../../../contexts/PermissionContext';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { getModuleTableTheme } from '../../../theme/tableTheme';
import { auditLogsAPI, comptoirCaisseAPI, comptoirPaiementsAPI } from '../../../services/api';
import { useComptoirCaisse, useComptoirCaisseMeta, useInvalidateComptoir } from '../../../hooks/queries/useComptoir';
import { ComptoirCaisseImpact, ComptoirCaisseMove, ComptoirCaissePeriode } from '../../../types/comptoir';
import { AnnulerCaisseDialog, CaisseOperationDialog, PaiementCaisseDialog, SupprimerCaisseDialog } from '../components/CaisseDialogs';
import { COLOR, dh, fDate, num, primaryBtnSx } from '../ui';

const MINI = 'Caisse';
const PERIODES: [ComptoirCaissePeriode, string][] = [
  ['jour', "Aujourd'hui"], ['semaine', 'Cette semaine'], ['mois', 'Ce mois'], ['tout', 'Tout'], ['perso', 'Période choisie'],
];
const SENS: [string, string][] = [['', 'Entrées et sorties'], ['entree', 'Entrées'], ['sortie', 'Sorties'], ['comptage', 'Comptages']];
const TONE = {
  entree: { label: 'Entrée', color: '#2E7D32' },
  sortie: { label: 'Sortie', color: '#D32F2F' },
  comptage: { label: 'Comptage', color: '#607D8B' },
};

/** Caisse: the workshop's cash — opening balance, entrées / sorties, comptages and the journal. */
const ComptoirCaisse: React.FC = () => {
  const { alertState, showError, showSuccess, showWarning, hideAlert } = useAlert();
  const { openPreview, pdfPreviewElement } = usePdfPreview();
  const { hasPermission } = usePermissions();
  const canAdd = hasPermission('Comptoir', 'add', MINI);
  const canEdit = hasPermission('Comptoir', 'edit', MINI);
  const canDelete = hasPermission('Comptoir', 'delete', MINI);
  const canSeeServices = hasPermission('Comptoir', 'view', 'Services & Projets');
  const invalidate = useInvalidateComptoir();

  const [periode, setPeriode] = useState<ComptoirCaissePeriode>('mois');
  const [du, setDu] = useState('');
  const [au, setAu] = useState('');
  const [sens, setSens] = useState('');
  const [categorie, setCategorie] = useState('');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const params = {
    periode, sens: sens || undefined, categorie: categorie || undefined, search: debounced || undefined,
    ...(periode === 'perso' ? { du: du || undefined, au: au || undefined } : {}),
  };
  const q = useComptoirCaisse(params);
  const { data: meta } = useComptoirCaisseMeta();
  const j = q.data;

  const [opSens, setOpSens] = useState<'entree' | 'sortie' | null>(null);
  const [annulOp, setAnnulOp] = useState<ComptoirCaisseMove | null>(null);
  const [editOp, setEditOp] = useState<ComptoirCaisseMove | null>(null);
  const [delOp, setDelOp] = useState<{ op: ComptoirCaisseMove; impact: ComptoirCaisseImpact } | null>(null);
  const [checking, setChecking] = useState<string | null>(null);
  const [payOp, setPayOp] = useState<{ op: ComptoirCaisseMove; action: 'edit' | 'delete' } | null>(null);

  /** Modifier / Supprimer first ask the server what depends on the operation. */
  const check = async (x: ComptoirCaisseMove, action: 'edit' | 'delete') => {
    setChecking(`${action}-${x.id}`);
    try {
      const impact = await comptoirCaisseAPI.impact(Number(x.id));
      if (action === 'delete') setDelOp({ op: x, impact });
      else if (!impact.modifiable) showWarning(impact.non_modifiable || '', `Modifier ${x.reference}`);
      else setEditOp(x);
    } catch (e: any) { showError(extractErrorMessage(e)); } finally { setChecking(null); }
  };

  const solde = num(j?.solde_actuel);
  const periodeLabel = PERIODES.find(([k]) => k === periode)?.[1] || '';
  const types = (meta?.types || []).filter((t) => !sens || t.sens === sens);

  /* ---------- documents ---------- */
  const printOp = (x: ComptoirCaisseMove) => {
    openPreview({
      getBlob: async () => (await import('../../../utils/comptoirBonsPdfReactPdf')).getBonCaissePDFBlob(x),
      fileName: `${x.reference}.pdf`,
    });
    auditLogsAPI.logPdfDownload('ComptoirCaisseOperation', Number(x.id), `Aperçu bon de caisse - ${x.reference}`);
  };
  const printBon = async (x: ComptoirCaisseMove) => {
    try {
      const p = await comptoirPaiementsAPI.get(x.paiement_id as number);
      openPreview({
        getBlob: async () => (await import('../../../utils/comptoirBonsPdfReactPdf')).getBonPaiementPDFBlob(p),
        fileName: `Bon_${x.reference}.pdf`,
      });
    } catch (e: any) { showError(extractErrorMessage(e)); }
  };
  const printJournal = () => {
    if (!j) return;
    openPreview({
      getBlob: async () => (await import('../../../utils/comptoirBonsPdfReactPdf')).getJournalCaissePDFBlob(j),
      fileName: `Journal_caisse_${new Date().toISOString().split('T')[0]}.pdf`,
    });
  };

  /* ---------- KPIs ---------- */
  const kpis = [
    { title: 'Solde actuel', value: dh(j?.solde_actuel), sub: 'selon le journal', color: COLOR, icon: <SoldeIcon /> },
    { title: 'Total des entrées', value: dh(j?.entrees), sub: periodeLabel, color: '#2E7D32', icon: <InIcon /> },
    { title: 'Total des sorties', value: dh(j?.sorties), sub: periodeLabel, color: '#D32F2F', icon: <OutIcon /> },
  ];

  const refDetails = (x: ComptoirCaisseMove) => [
    x.beneficiaire ? `${x.sens === 'entree' ? 'Remis par' : 'Bénéficiaire'} : ${x.beneficiaire}` : '',
    x.piece_ref ? `Pièce ${x.piece_ref}` : '',
    x.client_nom || '',
    x.service_reference || '',
  ].filter(Boolean).join(' · ');

  return (
    <Box>
      {/* ---------- header ---------- */}
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'flex-start' }} spacing={2} sx={{ mb: 2 }}>
        <Box sx={{ maxWidth: 760 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: COLOR }}>Caisse</Typography>
          <Typography variant="body2" color="text.secondary">
            Argent liquide de l'atelier. Les paiements clients en espèces entrent tout seuls (depuis « Encaisser ») ; les autres entrées et
            toutes les sorties se saisissent ici. En cas d'erreur, on annule l'opération avec un motif (elle reste visible, barrée), ou on la modifie / supprime
            si rien n'en dépend.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap={{ xs: 'wrap', md: 'nowrap' }} useFlexGap sx={{ flexShrink: 0, '& .MuiButton-root': { whiteSpace: 'nowrap' } }}>
          {canAdd && <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setOpSens('entree')}>Entrée</Button>}
          {canAdd && <Button variant="outlined" startIcon={<RemoveIcon />} onClick={() => setOpSens('sortie')}>Sortie</Button>}
          <Button variant="contained" startIcon={<PrintIcon />} onClick={printJournal} disabled={!j} sx={primaryBtnSx}>Journal</Button>
        </Stack>
      </Stack>

      {/* ---------- KPIs ---------- */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {kpis.map((k) => (
          <Grid item xs={12} sm={6} md key={k.title}>
            <GlassSurface accentColor={k.color} sx={{ p: 2, height: '100%' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary">{k.title}</Typography>
                  {j ? <Typography variant="h5" sx={{ fontWeight: 700 }} noWrap>{k.value}</Typography> : <Skeleton width={100} height={36} />}
                  <Typography variant="caption" color="text.secondary">{k.sub}</Typography>
                </Box>
                <Box sx={{ color: k.color, '& svg': { fontSize: 34 } }}>{k.icon}</Box>
              </Stack>
            </GlassSurface>
          </Grid>
        ))}
      </Grid>

      {/* ---------- journal ---------- */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Stack direction="row" alignItems="center" sx={{ px: 2, pt: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: COLOR, flex: 1 }}>Journal de caisse</Typography>
          <Typography variant="body2" color="text.secondary">{j ? `${j.operations.length} opération(s)` : ''}</Typography>
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ p: 2 }} flexWrap="wrap" useFlexGap>
          <TextField select size="small" label="Période" value={periode} onChange={(e) => setPeriode(e.target.value as ComptoirCaissePeriode)} sx={{ minWidth: 170 }}>
            {PERIODES.map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
          </TextField>
          {periode === 'perso' && (
            <>
              <TextField size="small" type="date" label="Du" InputLabelProps={{ shrink: true }} value={du} onChange={(e) => setDu(e.target.value)} />
              <TextField size="small" type="date" label="Au" InputLabelProps={{ shrink: true }} value={au} onChange={(e) => setAu(e.target.value)} />
            </>
          )}
          <TextField select size="small" label="Sens" value={sens} onChange={(e) => { setSens(e.target.value); setCategorie(''); }} sx={{ minWidth: 180 }}>
            {SENS.map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
          </TextField>
          {sens !== 'comptage' && (
            <TextField select size="small" label="Type" value={categorie} onChange={(e) => setCategorie(e.target.value)} sx={{ minWidth: 230 }}>
              <MenuItem value="">Tous les types</MenuItem>
              {types.map((t) => <MenuItem key={`${t.sens}-${t.value}`} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
          )}
          <TextField size="small" placeholder="N°, motif, bénéficiaire, client…" value={search} onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 220 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
              endAdornment: search && (
                <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')} aria-label="Effacer la recherche"><ClearIcon fontSize="small" /></IconButton></InputAdornment>
              ),
            }} />
        </Stack>
        {q.isFetching && <LinearProgress sx={{ height: 2, '& .MuiLinearProgress-bar': { bgcolor: COLOR }, bgcolor: alpha(COLOR, 0.1) }} />}
        <TableContainer sx={getModuleTableTheme('comptoir')}>
          <Table size="small" sx={{ minWidth: 1100 }}>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell><TableCell>N°</TableCell><TableCell>Type d'opération</TableCell><TableCell>Motif · référence</TableCell>
                <TableCell align="right">Entrée</TableCell><TableCell align="right">Sortie</TableCell><TableCell align="right">Solde</TableCell>
                <TableCell align="right" width={190} />
              </TableRow>
            </TableHead>
            <TableBody>
              {!j ? Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={i}>{Array.from({ length: 8 }).map((__, k) => <TableCell key={k}><Skeleton /></TableCell>)}</TableRow>
              )) : (
                <>
                  {j.operations.length === 0 && (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {j.filtre ? 'Aucune opération ne correspond aux filtres.' : 'Aucune opération sur cette période.'}
                    </TableCell></TableRow>
                  )}
                  {/* newest first: each row keeps its running balance, read bottom-up */}
                  {[...j.operations].reverse().map((x) => {
                    const tone = TONE[x.sens];
                    const cm = x.sens === 'comptage';
                    const strike = x.annule ? { textDecoration: 'line-through', color: 'text.disabled' } : {};
                    return (
                      <TableRow key={String(x.id)} hover sx={{ opacity: x.annule ? 0.75 : 1 }}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2">{fDate(x.date)}</Typography>
                          <Typography variant="caption" color="text.secondary">{x.user_name || ''}</Typography>
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, color: COLOR, whiteSpace: 'nowrap', ...strike }}>{x.reference}</TableCell>
                        <TableCell>
                          <Chip label={tone.label} size="small" variant="outlined"
                            sx={{ height: 22, fontWeight: 700, color: tone.color, borderColor: alpha(tone.color, 0.6), bgcolor: alpha(tone.color, 0.06) }} />
                          <Typography variant="caption" color="text.secondary" display="block">{x.libelle}</Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 420 }}>
                          <Typography variant="body2" sx={strike}>
                            {cm
                              ? <>Compté {dh(x.montant)} · théorique {dh(x.theorique)} · écart <b>{num(x.ecart) > 0 ? '+' : ''}{dh(x.ecart)}</b></>
                              : x.motif}
                          </Typography>
                          {cm && x.motif && <Typography variant="caption" color="text.secondary" display="block">{x.motif}</Typography>}
                          {refDetails(x) && <Typography variant="caption" color="text.secondary" display="block">{refDetails(x)}</Typography>}
                          {x.annule && (
                            <Typography variant="caption" color="warning.main" display="block">
                              Annulée le {fDate(x.annule_le)} par {x.annule_par || '—'} : {x.motif_annulation}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap', color: 'success.main', ...strike }}>
                          {x.sens === 'entree' ? dh(x.montant) : ''}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap', color: 'error.main', ...strike }}>
                          {x.sens === 'sortie' ? dh(x.montant) : ''}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700, whiteSpace: 'nowrap' }}>{x.annule ? '—' : dh(x.solde)}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {x.auto ? (
                            <>
                              <Tooltip title="Paiement client en espèces : imprimer son bon">
                                <Button size="small" onClick={() => printBon(x)} sx={{ color: COLOR, textTransform: 'none' }}>via {x.reference}</Button>
                              </Tooltip>
                              {canEdit && (
                                <Tooltip title="Modifier">
                                  <IconButton size="small" onClick={() => setPayOp({ op: x, action: 'edit' })} sx={{ color: COLOR }} aria-label={`Modifier ${x.reference}`}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {canDelete && (
                                <Tooltip title="Supprimer">
                                  <IconButton size="small" color="error" onClick={() => setPayOp({ op: x, action: 'delete' })} aria-label={`Supprimer ${x.reference}`}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          ) : (
                            <>
                              <Tooltip title="Imprimer le bon de caisse">
                                <IconButton size="small" onClick={() => printOp(x)} sx={{ color: COLOR }} aria-label={`Imprimer ${x.reference}`}><PrintIcon fontSize="small" /></IconButton>
                              </Tooltip>
                              {canEdit && (
                                <Tooltip title="Modifier">
                                  <span>
                                    <IconButton size="small" onClick={() => check(x, 'edit')} disabled={!!checking} sx={{ color: COLOR }} aria-label={`Modifier ${x.reference}`}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                              {canEdit && !x.annule && (
                                <Tooltip title="Annuler l'opération">
                                  <IconButton size="small" color="warning" onClick={() => setAnnulOp(x)} aria-label={`Annuler ${x.reference}`}><UndoIcon fontSize="small" /></IconButton>
                                </Tooltip>
                              )}
                              {canDelete && (
                                <Tooltip title="Supprimer">
                                  <span>
                                    <IconButton size="small" color="error" onClick={() => check(x, 'delete')} disabled={!!checking} aria-label={`Supprimer ${x.reference}`}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {j.periode.du && !j.filtre && (
                    <TableRow sx={{ bgcolor: alpha(COLOR, 0.04) }}>
                      <TableCell colSpan={6} sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 0.4 }}>
                        SOLDE AU DÉBUT DE LA PÉRIODE ({fDate(j.periode.du)})
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{dh(j.solde_debut)}</TableCell>
                      <TableCell />
                    </TableRow>
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ---------- dialogs ---------- */}
      <CaisseOperationDialog
        open={!!opSens} sens={opSens || 'entree'} solde={solde} depuis={j?.ouverture?.depuis || null}
        onClose={() => setOpSens(null)}
        onSaved={(x, print) => {
          setOpSens(null);
          invalidate();
          if (print) printOp(x);
          else showSuccess(`${x.reference} enregistrée`);
        }}
      />
      <CaisseOperationDialog
        open={!!editOp} sens={editOp?.sens === 'sortie' ? 'sortie' : 'entree'} op={editOp} solde={solde} depuis={j?.ouverture?.depuis || null}
        onClose={() => setEditOp(null)}
        onSaved={(x, print) => {
          setEditOp(null);
          invalidate();
          if (print) printOp(x);
          else showSuccess(`${x.reference} modifiée`);
        }}
      />
      <SupprimerCaisseDialog
        open={!!delOp} op={delOp?.op || null} impact={delOp?.impact || null} onClose={() => setDelOp(null)}
        onDeleted={(ref) => { setDelOp(null); invalidate(); showSuccess(`${ref} supprimée`); }}
      />
      <PaiementCaisseDialog open={!!payOp} op={payOp?.op || null} action={payOp?.action || 'edit'} canOpen={canSeeServices} onClose={() => setPayOp(null)} />
      <AnnulerCaisseDialog
        open={!!annulOp} op={annulOp} onClose={() => setAnnulOp(null)}
        onSaved={(x) => { setAnnulOp(null); invalidate(); showSuccess(`${x.reference} annulée`); }}
      />
      <AlertDialog open={alertState.open} type={alertState.type} title={alertState.title} message={alertState.message}
        onClose={hideAlert} onConfirm={alertState.onConfirm} confirmText={alertState.confirmText}
        cancelText={alertState.cancelText} showCancel={alertState.showCancel} />
      {pdfPreviewElement}
    </Box>
  );
};

export default ComptoirCaisse;
